/**
 * MaterialExplorer Page - Premium Redesign V3
 * ============================================
 * All content and styles CMS-driven via Blueprint context.
 * 
 * Features:
 * - Full-width hero background with marble environment
 * - Category pills inline (dropdown on mobile)
 * - Color filters with material slab thumbnails
 * - List view with small slab image on left
 * - Working quick filters
 * - Server-side pagination with page size selector
 * - Scroll-to-top on page change
 * - Platform palette: black #0a0a0a, lighter #121212, accent #B08B5C
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBlueprint } from '../context/BlueprintContext';
import { API } from '../App';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMaterialImageUrl, PLACEHOLDER_IMAGE } from '../utils/materialImages';
import { 
  Search, Heart, LayoutGrid, List, ChevronLeft, ChevronRight,
  Sparkles, Sliders, Circle, MapPin, RotateCcw, ChevronDown, ChevronUp,
  ArrowRight, Filter
} from 'lucide-react';

// Sanitize image URL helper
const sanitizeImageUrl = (url) => {
  if (!url) return PLACEHOLDER_IMAGE;
  if (url.startsWith('/api/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${url}`;
  }
  return url;
};

// Default CMS Configuration
const DEFAULT_EXPLORER_CONFIG = {
  header: {
    label: 'MATERIAL DISCOVERY',
    title: 'Esplora Materiali',
    subtitle: 'Scopri la più ampia collezione di pietre naturali. Filtra, esplora e lasciati ispirare dalle superfici più straordinarie al mondo.',
    searchPlaceholder: 'Cerca materiali, colori, tipologie...',
    heroBackgroundUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80'
  },
  categories: {
    title: 'Categorie',
    allLabel: 'Tutti i materiali',
    items: [
      { id: 'all', label: 'Tutti i materiali' },
      { id: 'Marble', label: 'Marmo' },
      { id: 'Quartzite', label: 'Quarzite' },
      { id: 'Onyx', label: 'Onice' },
      { id: 'Granite', label: 'Granito' },
      { id: 'Limestone', label: 'Limestone' },
      { id: 'Travertine', label: 'Travertino' }
    ]
  },
  quickFilters: {
    title: 'FILTRI RAPIDI',
    items: [
      { id: 'calm', label: 'Superfici Calme', icon: 'circle', description: 'Materiali uniformi con texture delicata' },
      { id: 'statement', label: 'Statement Materials', icon: 'sparkles', description: 'Materiali con forte impatto visivo' },
      { id: 'contrast', label: 'Alto Contrasto', icon: 'sliders', description: 'Materiali con venature marcate' }
    ]
  },
  categoryFilters: {
    title: 'CATEGORIE',
    items: [
      { id: 'Marble', label: 'Marmo' },
      { id: 'Quartzite', label: 'Quarzite' },
      { id: 'Onyx', label: 'Onice' },
      { id: 'Granite', label: 'Granito' },
      { id: 'Limestone', label: 'Limestone' },
      { id: 'Travertine', label: 'Travertino' },
      { id: 'Other', label: 'Altre pietre' }
    ]
  },
  colorFilters: {
    title: 'COLORI',
    items: [
      { id: 'white', label: 'Bianco', count: 49 },
      { id: 'grey', label: 'Grigio', count: 40 },
      { id: 'beige', label: 'Beige', count: 32 },
      { id: 'green', label: 'Verde', count: 28 },
      { id: 'black', label: 'Nero', count: 18 },
      { id: 'blue', label: 'Blu', count: 18 },
      { id: 'brown', label: 'Marrone', count: 18 },
      { id: 'red', label: 'Rosso', count: 18 },
      { id: 'gold', label: 'Oro / Arancio', count: 14 },
      { id: 'pink', label: 'Rosa', count: 7 }
    ]
  },
  results: {
    countSingular: '1 materiale',
    countPlural: '{count} materiali',
    sortLabel: 'Ordina per:',
    sortOptions: [
      { id: 'featured', label: 'In evidenza' },
      { id: 'name_asc', label: 'Nome A-Z' },
      { id: 'name_desc', label: 'Nome Z-A' }
    ],
    clearFilters: 'Pulisci filtri',
    viewDetails: 'Vedi dettaglio'
  },
  pagination: {
    itemsPerPage: 24,
    pageSizeOptions: [12, 24, 48, 96]
  }
};

// Stone type labels
const STONE_TYPE_LABELS = {
  'Marble': 'MARMO',
  'Quartzite': 'QUARZITE',
  'Onyx': 'ONICE',
  'Granite': 'GRANITO',
  'Limestone': 'LIMESTONE',
  'Travertine': 'TRAVERTINO',
  'Semiprecious': 'SEMIPREZIOSA'
};

// Calm materials (uniform colors)
const CALM_COLORS = ['white', 'grey', 'beige', 'cream'];
const CALM_TYPES = ['Limestone', 'Travertine'];

// Statement materials (bold patterns)
const STATEMENT_TYPES = ['Onyx', 'Semiprecious', 'Quartzite'];
const STATEMENT_COLORS = ['green', 'blue', 'red', 'gold', 'purple'];

// High contrast types
const CONTRAST_TYPES = ['Marble', 'Quartzite'];
const CONTRAST_COLORS = ['black', 'white'];

// Icon mapping
const ICON_MAP = {
  circle: Circle,
  sparkles: Sparkles,
  sliders: Sliders
};

/**
 * Color Filter Slab - Shows random material image for that color
 */
const ColorFilterSlab = ({ colorId, label, isSelected, onClick, materials, count }) => {
  const colorMaterials = useMemo(() => {
    return materials.filter(m => {
      const matColor = (m.color_family || m.dominant_color || m.color || '').toLowerCase();
      return matColor.includes(colorId.toLowerCase());
    });
  }, [materials, colorId]);
  
  // Pick a random sample material (changes on each render/refresh)
  const sampleMaterial = useMemo(() => {
    if (colorMaterials.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * colorMaterials.length);
    return colorMaterials[randomIndex];
  }, [colorMaterials]);
  
  const imageUrl = sampleMaterial ? getMaterialImageUrl(sampleMaterial) : null;
  const materialCount = colorMaterials.length;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full group transition-all ${
        isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100'
      }`}
    >
      <div className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
        isSelected ? 'border-[#B08B5C]' : 'border-transparent'
      }`}>
        {imageUrl ? (
          <img src={imageUrl} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#2a2a2a]" />
        )}
      </div>
      <div className="flex-1 text-left">
        <span className={`text-sm block ${isSelected ? 'text-white font-medium' : 'text-gray-300'}`}>
          {label}
        </span>
        <span className="text-xs text-gray-500">{materialCount} materiali</span>
      </div>
      {isSelected && <div className="ml-auto w-2 h-2 rounded-full bg-[#B08B5C]" />}
    </button>
  );
};

/**
 * Material Card - Grid View
 */
const MaterialCardGrid = ({ material, onFavorite }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  
  const imageUrl = getMaterialImageUrl(material);
  const stoneType = material.stone_type || 'Marble';
  const stoneLabel = STONE_TYPE_LABELS[stoneType] || stoneType.toUpperCase();
  const colorFamily = material.color_family || material.dominant_color || 'beige';
  const origin = material.origin || 'Italia';
  
  const handleClick = () => navigate(`/materials/${material.material_id}`);
  
  const handleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
    onFavorite?.(material.material_id);
  };
  
  return (
    <div className="group cursor-pointer" onClick={handleClick}>
      <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-[#121212] mb-3">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={material.name} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">No image</div>
        )}
        <button
          onClick={handleFavorite}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
            isFavorite ? 'bg-[#B08B5C] text-black' : 'bg-black/40 text-white hover:bg-black/60'
          }`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
        <div className="absolute bottom-3 left-3 px-3 py-1 rounded bg-[#121212]/80 backdrop-blur-sm">
          <span className="text-[10px] font-bold tracking-wider text-white">{stoneLabel}</span>
        </div>
      </div>
      <div>
        <h3 className="text-white font-medium mb-2 group-hover:text-[#B08B5C] transition-colors">{material.name}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span className="capitalize">{colorFamily}</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
          <MapPin className="w-3 h-3" />
          <span>{origin}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Material Card - List View (Small slab on left, info on right)
 */
const MaterialCardList = ({ material, onFavorite, config }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  
  const imageUrl = getMaterialImageUrl(material);
  const stoneType = material.stone_type || 'Marble';
  const stoneLabel = STONE_TYPE_LABELS[stoneType] || stoneType.toUpperCase();
  const colorFamily = material.color_family || material.dominant_color || 'beige';
  const origin = material.origin || 'Italia';
  
  const handleClick = () => navigate(`/materials/${material.material_id}`);
  
  const handleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
    onFavorite?.(material.material_id);
  };
  
  return (
    <div 
      className="flex gap-4 p-4 bg-[#121212] rounded-xl border border-[#1a1a1a] hover:border-[#2a2a2a] transition-all cursor-pointer group"
      onClick={handleClick}
    >
      {/* Small slab image - rectangular like a slab */}
      <div className="w-32 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-[#1a1a1a]">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={material.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No image</div>
        )}
      </div>
      
      {/* Info section */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[10px] font-bold tracking-wider text-[#B08B5C] block mb-1">{stoneLabel}</span>
            <h3 className="text-white font-medium text-lg group-hover:text-[#B08B5C] transition-colors">{material.name}</h3>
          </div>
          <button
            onClick={handleFavorite}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
              isFavorite ? 'bg-[#B08B5C] text-black' : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>
        
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
          <span className="capitalize">{colorFamily}</span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {origin}
          </span>
        </div>
      </div>
      
      {/* CTA Button on right */}
      <div className="flex items-center flex-shrink-0">
        <button 
          onClick={handleClick}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] text-white hover:bg-[#B08B5C] hover:text-black transition-all text-sm font-medium"
        >
          {config?.results?.viewDetails || 'Vedi dettaglio'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Main MaterialExplorer Component
 */
const MaterialExplorer = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getValue } = useBlueprint();
  const gridRef = useRef(null);
  
  const config = useMemo(() => {
    const cmsConfig = getValue('ui.pages.materialExplorer', null);
    return { ...DEFAULT_EXPLORER_CONFIG, ...cmsConfig };
  }, [getValue]);
  
  // State
  const [materials, setMaterials] = useState([]);
  const [allMaterialsForFilters, setAllMaterialsForFilters] = useState([]); // For color filter thumbnails
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state - server-side
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || config.pagination.itemsPerPage);
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [activeQuickFilters, setActiveQuickFilters] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  
  // Mobile filter dropdown
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    quickFilters: true,
    categories: true,
    colors: true
  });
  
  // Scroll to TOP of page on page change
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Fetch materials with server-side pagination
  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('page_size', pageSize.toString());
        
        if (searchQuery.trim()) params.append('search', searchQuery.trim());
        
        // Category filter - prioritize sidebar checkboxes, then category pill
        if (selectedCategories.length > 0) {
          // Multiple categories - need to filter client-side after fetching
          // For single category, use server-side
          if (selectedCategories.length === 1) {
            params.append('stone_type', selectedCategories[0]);
          }
        } else if (activeCategory !== 'all') {
          params.append('stone_type', activeCategory);
        }
        
        // Color filter - single color uses server-side
        if (selectedColors.length === 1) {
          params.append('color', selectedColors[0]);
        }
        
        const response = await axios.get(`${API}/materials?${params.toString()}`);
        const data = response.data;
        
        // Handle paginated response
        if (data.materials) {
          let mats = data.materials;
          
          // Client-side filtering for multiple categories
          if (selectedCategories.length > 1) {
            mats = mats.filter(m => {
              const matType = (m.stone_type || '').toLowerCase();
              return selectedCategories.some(c => matType.includes(c.toLowerCase()));
            });
          }
          
          // Client-side filtering for multiple colors
          if (selectedColors.length > 1) {
            mats = mats.filter(m => {
              const matColor = (m.color_family || m.dominant_color || m.color || '').toLowerCase();
              return selectedColors.some(c => matColor.includes(c.toLowerCase()));
            });
          }
          
          setMaterials(mats);
          setTotalMaterials(selectedCategories.length > 1 || selectedColors.length > 1 ? mats.length : data.total || 0);
          setTotalPages(selectedCategories.length > 1 || selectedColors.length > 1 ? 1 : data.total_pages || 1);
        } else if (Array.isArray(data)) {
          // Fallback for old API format
          setMaterials(data);
          setTotalMaterials(data.length);
          setTotalPages(1);
        }
      } catch (err) {
        console.error('Failed to fetch materials:', err);
        setError('Errore nel caricamento dei materiali');
      }
      setLoading(false);
    };
    
    fetchMaterials();
  }, [currentPage, pageSize, searchQuery, activeCategory, selectedCategories, selectedColors]);
  
  // Fetch all materials once for color filter thumbnails
  useEffect(() => {
    const fetchAllForFilters = async () => {
      try {
        // First call to get total count
        const firstResponse = await axios.get(`${API}/materials?page=1&page_size=96`);
        const firstData = firstResponse.data;
        let allMaterials = firstData.materials || [];
        const totalPages = firstData.total_pages || 1;
        
        // Fetch remaining pages if needed
        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(axios.get(`${API}/materials?page=${page}&page_size=96`));
          }
          const responses = await Promise.all(promises);
          for (const resp of responses) {
            const mats = resp.data.materials || [];
            allMaterials = [...allMaterials, ...mats];
          }
        }
        
        console.log(`[MaterialExplorer] Loaded ${allMaterials.length} materials for filters`);
        setAllMaterialsForFilters(allMaterials);
      } catch (err) {
        console.error('Failed to fetch materials for filters:', err);
      }
    };
    fetchAllForFilters();
  }, []);
  
  // Update URL when pagination/filters change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('size', pageSize.toString());
    if (searchQuery) params.set('search', searchQuery);
    if (activeCategory !== 'all') params.set('category', activeCategory);
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchQuery, activeCategory]);
  
  // Client-side filtering for quick filters (applied after server results)
  const filteredMaterials = useMemo(() => {
    let result = [...materials];
    
    // Quick filters only (category/color already filtered in fetch)
    if (activeQuickFilters.includes('calm')) {
      result = result.filter(m => {
        const color = (m.color_family || m.dominant_color || m.color || '').toLowerCase();
        const type = (m.stone_type || '').toLowerCase();
        return CALM_TYPES.map(t => t.toLowerCase()).includes(type) || CALM_COLORS.some(c => color.includes(c));
      });
    }
    
    if (activeQuickFilters.includes('statement')) {
      result = result.filter(m => {
        const color = (m.color_family || m.dominant_color || m.color || '').toLowerCase();
        const type = (m.stone_type || '').toLowerCase();
        return STATEMENT_TYPES.map(t => t.toLowerCase()).includes(type) || STATEMENT_COLORS.some(c => color.includes(c));
      });
    }
    
    if (activeQuickFilters.includes('contrast')) {
      result = result.filter(m => {
        const color = (m.color_family || m.dominant_color || m.color || '').toLowerCase();
        const type = (m.stone_type || '').toLowerCase();
        return CONTRAST_TYPES.map(t => t.toLowerCase()).includes(type) && CONTRAST_COLORS.some(c => color.includes(c));
      });
    }
    
    // Sorting
    switch (sortBy) {
      case 'name_asc':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name_desc':
        result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      default:
        break;
    }
    
    return result;
  }, [materials, activeQuickFilters, sortBy]);
  
  // Handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      scrollToTop();
    }
  };
  
  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1);
    scrollToTop();
  };
  
  // Handlers
  const toggleQuickFilter = (id) => {
    setActiveQuickFilters(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };
  
  const toggleCategory = (id) => {
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };
  
  const toggleColor = (id) => {
    setSelectedColors(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
    setCurrentPage(1);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  
  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };
  
  const clearAllFilters = () => {
    setSearchQuery('');
    setActiveCategory('all');
    setActiveQuickFilters([]);
    setSelectedCategories([]);
    setSelectedColors([]);
    setCurrentPage(1);
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const handleFavorite = (materialId) => {
    console.log('Favorite:', materialId);
  };
  
  const resultsText = totalMaterials === 0 
    ? 'Nessun materiale trovato'
    : totalMaterials === 1 
      ? '1 materiale' 
      : `${totalMaterials} materiali`;
  
  const pageInfo = totalPages > 1 ? ` • Pagina ${currentPage} di ${totalPages}` : '';
  
  const getPaginationRange = () => {
    const range = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + showPages - 1);
    if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section - Full Width Background */}
        <section className="relative h-[420px] overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={config.header.heroBackgroundUrl}
              alt="Marble interior"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          </div>
          
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8 h-full flex items-center">
            <div className="max-w-xl">
              <span className="text-[10px] font-bold tracking-[3px] uppercase text-[#B08B5C] mb-4 block">
                {config.header.label}
              </span>
              <h1 className="text-4xl lg:text-5xl font-light text-white mb-4">
                {config.header.title}
              </h1>
              <p className="text-gray-300 mb-8 leading-relaxed">
                {config.header.subtitle}
              </p>
              
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={config.header.searchPlaceholder}
                  className="w-full pl-12 pr-4 py-3.5 bg-[#121212]/90 backdrop-blur-sm border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#B08B5C]/50"
                />
              </div>
              
              {/* Category Pills removed - already in sidebar */}
              
              {/* Mobile Category Dropdown */}
              <div className="md:hidden relative">
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#121212]/90 backdrop-blur-sm border border-[#2a2a2a] rounded-lg text-white"
                >
                  <span>{config.categories.items.find(c => c.id === activeCategory)?.label || 'Tutti i materiali'}</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showCategoryDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-[#121212] border border-[#2a2a2a] rounded-lg overflow-hidden z-20">
                    {config.categories.items.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          handleCategoryChange(cat.id);
                          setShowCategoryDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm transition-all ${
                          activeCategory === cat.id
                            ? 'bg-[#B08B5C]/20 text-[#B08B5C]'
                            : 'text-gray-300 hover:bg-[#1a1a1a]'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        
        {/* Main Content */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-10">
          {/* Mobile Filter Button */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-[#2a2a2a] rounded-lg text-white"
            >
              <Filter className="w-4 h-4" />
              Filtri
              {(activeQuickFilters.length > 0 || selectedCategories.length > 0 || selectedColors.length > 0) && (
                <span className="w-5 h-5 rounded-full bg-[#B08B5C] text-black text-xs flex items-center justify-center">
                  {activeQuickFilters.length + selectedCategories.length + selectedColors.length}
                </span>
              )}
            </button>
          </div>
          
          <div className="flex gap-8">
            {/* Left Sidebar - Filters (Hidden on mobile, shown in dropdown) */}
            <aside className={`${showMobileFilters ? 'fixed inset-0 z-50 bg-[#0a0a0a] p-6 overflow-y-auto' : 'hidden'} lg:block lg:relative lg:w-[240px] lg:flex-shrink-0`}>
              {/* Mobile close button */}
              {showMobileFilters && (
                <div className="lg:hidden flex items-center justify-between mb-6">
                  <span className="text-white font-medium">Filtri</span>
                  <button onClick={() => setShowMobileFilters(false)} className="text-gray-400">
                    Chiudi
                  </button>
                </div>
              )}
              
              {/* Quick Filters */}
              <div className="mb-8">
                <button 
                  onClick={() => toggleSection('quickFilters')}
                  className="flex items-center justify-between w-full mb-4"
                >
                  <span className="text-[10px] font-bold tracking-[2px] text-gray-400">
                    {config.quickFilters.title}
                  </span>
                  {expandedSections.quickFilters ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.quickFilters && (
                  <div className="space-y-2">
                    {config.quickFilters.items.map((filter) => {
                      const Icon = ICON_MAP[filter.icon] || Circle;
                      const isActive = activeQuickFilters.includes(filter.id);
                      return (
                        <button
                          key={filter.id}
                          onClick={() => toggleQuickFilter(filter.id)}
                          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm transition-all ${
                            isActive
                              ? 'bg-[#B08B5C]/20 text-[#B08B5C] border border-[#B08B5C]/30'
                              : 'bg-[#121212] text-gray-400 hover:bg-[#1a1a1a] border border-transparent'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {filter.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Category Checkboxes */}
              <div className="mb-8">
                <button 
                  onClick={() => toggleSection('categories')}
                  className="flex items-center justify-between w-full mb-4"
                >
                  <span className="text-[10px] font-bold tracking-[2px] text-gray-400">
                    {config.categoryFilters.title}
                  </span>
                  {expandedSections.categories ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.categories && (
                  <div className="space-y-3">
                    {config.categoryFilters.items.map((cat) => (
                      <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                          selectedCategories.includes(cat.id)
                            ? 'bg-[#B08B5C] border-[#B08B5C]'
                            : 'border-[#3a3a3a] group-hover:border-gray-400'
                        }`}>
                          {selectedCategories.includes(cat.id) && (
                            <svg className="w-3 h-3 text-black" viewBox="0 0 12 12">
                              <path fill="currentColor" d="M10.28 2.28L4.5 8.06 1.72 5.28a1 1 0 00-1.44 1.44l3.5 3.5a1 1 0 001.44 0l6.5-6.5a1 1 0 00-1.44-1.44z"/>
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${selectedCategories.includes(cat.id) ? 'text-white' : 'text-gray-400'}`}>
                          {cat.label}
                        </span>
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                          className="hidden"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Color Family */}
              <div className="mb-8">
                <button 
                  onClick={() => toggleSection('colors')}
                  className="flex items-center justify-between w-full mb-4"
                >
                  <span className="text-[10px] font-bold tracking-[2px] text-gray-400">
                    {config.colorFilters.title}
                  </span>
                  {expandedSections.colors ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.colors && (
                  <div className="space-y-3">
                    {config.colorFilters.items.map((color) => (
                      <ColorFilterSlab
                        key={color.id}
                        colorId={color.id}
                        label={color.label}
                        isSelected={selectedColors.includes(color.id)}
                        onClick={() => toggleColor(color.id)}
                        materials={allMaterialsForFilters}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Clear Filters */}
              <button
                onClick={clearAllFilters}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#121212] text-gray-400 hover:bg-[#1a1a1a] hover:text-white transition-all border border-[#2a2a2a]"
              >
                <RotateCcw className="w-4 h-4" />
                {config.results.clearFilters}
              </button>
              
              {/* Mobile apply button */}
              {showMobileFilters && (
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="lg:hidden w-full mt-4 py-3 rounded-lg bg-[#B08B5C] text-black font-medium"
                >
                  Applica filtri
                </button>
              )}
            </aside>
            
            {/* Main Content Area */}
            <div className="flex-1 min-w-0" ref={gridRef}>
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <span className="text-gray-400 text-sm">{resultsText}{pageInfo}</span>
                
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Page Size Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">Mostra:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      className="appearance-none bg-[#121212] border border-[#2a2a2a] rounded-lg px-3 py-2 pr-8 text-white text-sm focus:outline-none focus:border-[#B08B5C]/50"
                    >
                      {config.pagination.pageSizeOptions.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* View Toggle */}
                  <div className="flex bg-[#121212] rounded-lg p-1 border border-[#2a2a2a]">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#1a1a1a] text-white' : 'text-gray-500'}`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#1a1a1a] text-white' : 'text-gray-500'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-[#121212] border border-[#2a2a2a] rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-[#B08B5C]/50"
                    >
                      {config.results.sortOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>{config.results.sortLabel} {opt.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              {/* Material Grid/List */}
              {loading ? (
                <div className={`${viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6' : 'space-y-4'}`}>
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      {viewMode === 'grid' ? (
                        <>
                          <div className="aspect-[4/5] bg-[#121212] rounded-xl mb-3" />
                          <div className="h-4 bg-[#121212] rounded w-3/4 mb-2" />
                          <div className="h-3 bg-[#121212] rounded w-1/2" />
                        </>
                      ) : (
                        <div className="flex gap-4 p-4 bg-[#121212] rounded-xl">
                          <div className="w-32 h-24 bg-[#1a1a1a] rounded-lg" />
                          <div className="flex-1">
                            <div className="h-4 bg-[#1a1a1a] rounded w-1/3 mb-2" />
                            <div className="h-6 bg-[#1a1a1a] rounded w-2/3 mb-2" />
                            <div className="h-3 bg-[#1a1a1a] rounded w-1/4" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <p className="text-gray-400 mb-4">{error}</p>
                  <button onClick={() => window.location.reload()} className="text-[#B08B5C]">Riprova</button>
                </div>
              ) : filteredMaterials.length === 0 ? (
                <div className="text-center py-20 bg-[#121212] rounded-xl">
                  <p className="text-gray-400 mb-4">Nessun materiale trovato</p>
                  <button onClick={clearAllFilters} className="text-[#B08B5C]">Rimuovi filtri</button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredMaterials.map((material) => (
                    <MaterialCardGrid 
                      key={material.material_id} 
                      material={material} 
                      onFavorite={handleFavorite}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMaterials.map((material) => (
                    <MaterialCardList 
                      key={material.material_id} 
                      material={material} 
                      onFavorite={handleFavorite}
                      config={config}
                    />
                  ))}
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-lg bg-[#121212] text-gray-400 flex items-center justify-center hover:bg-[#1a1a1a] disabled:opacity-50 border border-[#2a2a2a]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {getPaginationRange().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all border ${
                        page === currentPage
                          ? 'bg-[#B08B5C] text-black border-[#B08B5C]'
                          : 'bg-[#121212] text-gray-400 hover:bg-[#1a1a1a] border-[#2a2a2a]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="text-gray-500">...</span>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className="w-10 h-10 rounded-lg bg-[#121212] text-gray-400 flex items-center justify-center hover:bg-[#1a1a1a] border border-[#2a2a2a]"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-lg bg-[#121212] text-gray-400 flex items-center justify-center hover:bg-[#1a1a1a] disabled:opacity-50 border border-[#2a2a2a]"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default MaterialExplorer;
